import React, { memo, useState, CSSProperties, useCallback } from 'react';
import styled from 'styled-components';
import BarLoader from 'react-spinners/BarLoader';
import usePageTitle from '../../utils/use-page-title';

const StyledInput = styled.input`
  display: block;
  margin-bottom: 1em;
  padding: 0.5em;
  width: 400px;
`;

const loaderCSSOverride: CSSProperties = {
  display: 'inline-block',
  marginLeft: '2em'
};

const StyledSelect = styled.select`
  display: block;
  margin-bottom: 1em;
  padding: 0.5em;
  width: 200px;
`;

const StyledErrorMessage = styled.div`
  color: red;
`;

const StyledResults = styled.code`
    color: #000000;
    background: #f8f9fa;
    display: block;
    padding: 1em;
    margin: 1em 0 0;
    width: 600px;
`;

const appleNewsServiceEndpoint = `${process.env.REACT_APP_BASE_API_URL}apple-news-service/triggerArticleUpdate`;

const AppleNews: React.FC = () => {
  usePageTitle('Apple News');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formSubmissionResults, setFormSubmissionResults] = useState('');

  const submitArticle = useCallback(async (e) => {
    e.preventDefault();
    const { arc_article_id, story_key } =  e.target.elements;
    const arcId = arc_article_id?.value;
    const storyKey = story_key?.value;

    setErrorMessage('');
    setFormSubmissionResults('');

    if (!(arcId && storyKey)) {
      setErrorMessage("Please provide both an Arc article ID and an action to take");
      return;
    }

    // Make sure arcId has the correct length for valid Arc IDs (26 alphanumeric characters)
    const arcIdPattern = /^[A-Z0-9]{26}$/g;
    if (!arcIdPattern.test(arcId)) {
      setErrorMessage('Please enter a valid Arc ID');
      return;
    }

    setIsLoading(true);

    // make api call
    try {
      const response = await fetch(appleNewsServiceEndpoint, {
        method: 'POST',
        body: JSON.stringify({ id: arcId, key: storyKey })
      });

      setIsLoading(false);
      const responseAsJson = await response.json();
      const formattedResponse = JSON.stringify(responseAsJson).replace(/,/g, '<br />').replace(/[\{\}]/g, '');
      setFormSubmissionResults(formattedResponse);
    }
    catch (e) {
      setIsLoading(false);
      console.log(e);
      setFormSubmissionResults("Error making API request. See console log for details.");
    }
  }, [appleNewsServiceEndpoint]);

  const handleInputChange = useCallback(() => {
    setErrorMessage('');
  }, []);

  return (
    <>
      <h2 className="mb-4">Apple News Tools</h2>
      <>
        <h5>Manually process an article in Apple News</h5>
        <form onSubmit={submitArticle}>
          <>
            <label for="arc_article_id">Enter an Arc article ID</label>
            <StyledInput id="arc_article_id" onChange={handleInputChange} />

            <label for="story_key">Select an action</label>
            <StyledSelect id="story_key" onChange={handleInputChange}>
              <option value="" />
              <option value="story:first-publish">Publish</option>
              <option value="story:update">Update</option>
              <option value="story:delete">Delete</option>
            </StyledSelect>

            <button disabled={isLoading} type="submit">Submit</button>
            <BarLoader loading={isLoading} cssOverride={loaderCSSOverride} />
          </>

          {errorMessage && <StyledErrorMessage className="mt-3">{errorMessage}</StyledErrorMessage>}          
          {formSubmissionResults && <StyledResults dangerouslySetInnerHTML={{__html: formSubmissionResults}} />}
        </form>
      </>
    </>
  )
};

export default memo(AppleNews);
